<template>
  <dialog
    ref="dialog"
    :id="dialogId"
    class="custom-dialog"
    :style="dialogStyles"
    @close="handleClose"
    @click="handleClickClose">
    <button type="button" class="custom-dialog__close" @click.stop="handleClose">
      <icon-close />
    </button>
    <div v-if="dialogTitle" class="custom-dialog__title" v-html="dialogTitle" />
      <slot />
  </dialog>
</template>

<script setup lang="ts">
import IconClose from '~/assets/icons/IconClose.svg';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch
} from "#imports";

const dialogElement = useTemplateRef<HTMLDialogElement>('dialog');

const emit = defineEmits(['dialog-closed']);

const props = defineProps<{
  dialogTitle?: string,
  dialogId?: String
}>();

const scrollOffset = ref(null);
const isDialogOpen = ref(false);

const handleClose = () => {
  dialogElement.value?.close();
  cleanup();
  emit('dialog-closed');
}

const dialogStyles = computed(() => {
  return {
    'position': 'fixed',
    'top': '50%',
    'left': '50%',
    'transform': 'translate(-50%, -50%)',
    'margin': '0'
  };
})

// check for click outside dialog
function handleClickClose (event: MouseEvent) {
  const rect = dialogElement.value?.getBoundingClientRect();

  if (rect && (
    event.clientY < rect.top
    || event.clientY > rect.bottom
    || event.clientX < rect.left
    || event.clientX > rect.right)) {
    dialogElement.value?.close();
  }
}

onMounted(async () => {
  // Prevent body scroll when dialog is open
  document.body.style.overflow = 'hidden';
  isDialogOpen.value = true;

  await nextTick();

  dialogElement.value.showModal();
});

// Clean up when dialog is closed
const cleanup = () => {
  if (isDialogOpen.value) {
    document.body.style.overflow = '';
    isDialogOpen.value = false;
  }
};

// Cleanup on unmount
onUnmounted(() => {
  cleanup();
});
</script>

<style lang="scss">
@use "~/common/scss/container";
@use "~/common/scss/typography";

.custom-dialog {
  @include container.s();

  border: none;
  border-radius: 4px;
  font-size: 1rem;
  margin-top: 10vh;
  overflow: visible;
  padding: 1.5rem;
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: 380px;

  &::backdrop {
    background-color: #111111BB;
    backdrop-filter: blur(2px);
  }

  &__close {
    --fill-color: var(--color-black);

    align-items: center;
    background-color: var(--fill-color);
    border-radius: 50%;
    box-shadow: 0 0 .25rem #000;
    color: var(--color-white);
    display: flex;
    font-size: 2rem;
    justify-content: center;
    position: absolute;
    right: -.5em;
    top: -.5em;
    transition: color .25s;

    &:hover {
      color: var(--color-orange);
    }
  }

  &__title {
    @include typography.h4();

    margin-bottom: 1.5rem;
  }
}
</style>
